import React, { Suspense, useEffect } from 'react';
import './App.css';
import { BackToTop } from '@cb/apricot-react';
import { AccountType, AuthProvider, LoginManager, NoAccessPage } from '@satreg/auth';
import { AppTokenProvider, Environment, EnvironmentProvider, SATREG } from '@satreg/api';
import { CaptchaProvider } from '@satreg/captcha';
import { SessionTimeoutManager } from '@satreg/session-timeout-manager';
import { ErrorFallback, ServiceErrorBoundary } from '@satreg/error-handling';
import { DrupalInstanceProvider } from '@satreg/drupal';
import { CAPTCHA_SITE_KEY_NAME_DOMESTIC, CAPTCHA_SITE_KEY_NAME_INTERNATIONAL } from '@satreg/test-center';
import { AwsRum } from 'aws-rum-web';
import { BrowserRouter } from 'react-router-dom';
import { ScrollToTop } from '@satreg/scroll-to-top';
import { FeeWaiverProvider } from '@satreg/fee-waiver';
import { Spinner } from '@satreg/spinner';
import { WebAnalyticsProvider } from '@satreg/analytics';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import CBInputMethod from '@cb/apricot/CBInputMethod';
import GlobalHeader from '../components/GlobalHeader';
import GlobalFooter from '../components/GlobalFooter';
import ROUTES, { REGISTRATION_ROUTES } from '../routes';
import AppSelector from './AppSelector';
import AppFlowAnalyzer from './AppFlowAnalyzer';
import { APPLICATION_ID, APPLICATION_REGION, APPLICATION_VERSION, awsRumConfig } from '../constants/constants';
import '@cb/apricot/CBBase';
import '@cb/apricot/CBBand';
import '@cb/apricot/CBGrid';

const drupalUrl = import.meta.env.VITE_DRUPAL_URL || '';
const delayedRoutes = [
    REGISTRATION_ROUTES.paymentConfirmation,
    REGISTRATION_ROUTES.purchase,
    REGISTRATION_ROUTES.dashboard,
    REGISTRATION_ROUTES.manageDashboard,
    REGISTRATION_ROUTES.cancelConfirmation,
    REGISTRATION_ROUTES.landing,
];

export type AwsRumType = {
    awsRum?: AwsRum;
};

const LoginLanding: React.FC = () => {
    useEffect(() => {
        window.location.href = import.meta.env.VITE_MY_SAT_URL as string;
    }, []);

    return null;
};

const DRUPAL_GLOBAL = 'reg-global';
const DRUPAL_HOMEPAGE = 'reg-Homepage';

let awsRum: AwsRum | undefined;

try {
    awsRum = new AwsRum(APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, awsRumConfig);
    if (window.actionsAllowed && window.actionsAllowed.RUM) awsRum.enable();
    else awsRum.disable();
    // eslint-disable-next-line no-console
    console.log('initialized aws rum');
} catch (error) {
    // eslint-disable-next-line no-console
    console.log('Error initializing aws rum:', error);
    // Ignore errors thrown during CloudWatch RUM web client initialization
}

/**
 * Root of the application. Contains the header/footer, global application-level providers not specific to app functionality,
 * and the entry point for the registration functionality.
 */
const App: React.FC = () => {
    /**
     * Apricot - Activate input method tracking
     */
    useEffect(() => {
        CBInputMethod().init();
    }, []);

    return (
        <WebAnalyticsProvider>
            <GlobalHeader />
            <BrowserRouter basename="/">
                <DrupalInstanceProvider drupalUrl={drupalUrl} cachePages={[DRUPAL_GLOBAL, DRUPAL_HOMEPAGE]}>
                    <div id="satreg-app">
                        <ServiceErrorBoundary
                            awsRum={awsRum}
                            errorPage={
                                <div className="error-component justify-content-center align-items-center align-self-center display-flex">
                                    <ErrorFallback
                                        error={new Error('Error was caught in Error boundary')}
                                        redirectRoute={import.meta.env.VITE_MY_SAT_URL}
                                    />
                                </div>
                            }
                        >
                            <EnvironmentProvider
                                environment={import.meta.env.VITE_API_ENV as Environment}
                                appId={import.meta.env.VITE_APP_ID as string}
                                overrideAccessControlRule
                            >
                                <CaptchaProvider
                                    hcaptchaSiteKeys={{
                                        [CAPTCHA_SITE_KEY_NAME_INTERNATIONAL]: import.meta.env
                                            .VITE_HCAPTCHA_SITE_KEY_INTERNATIONAL as string,
                                        [CAPTCHA_SITE_KEY_NAME_DOMESTIC]: import.meta.env
                                            .VITE_HCAPTCHA_SITE_KEY_DOMESTIC as string,
                                    }}
                                    {...(import.meta.env.VITE_API_ENV === 'prod'
                                        ? {}
                                        : {
                                              testHcaptchaSiteKeys: {
                                                  [CAPTCHA_SITE_KEY_NAME_INTERNATIONAL]: import.meta.env
                                                      .VITE_TEST_HCAPTCHA_SITE_KEY_INTERNATIONAL as string,
                                                  [CAPTCHA_SITE_KEY_NAME_DOMESTIC]: import.meta.env
                                                      .VITE_TEST_HCAPTCHA_SITE_KEY_DOMESTIC as string,
                                              },
                                          })}
                                >
                                    <AuthProvider directoryName={SATREG}>
                                        <AppFlowAnalyzer>
                                            <AppTokenProvider>
                                                <div className="App">
                                                    <LoginManager
                                                        appId={import.meta.env.VITE_APP_ID as string}
                                                        allowedAccountTypes={[AccountType.STUDENT]}
                                                        awsDomain={import.meta.env.VITE_AWS_DOMAIN as string}
                                                        loginRoute={ROUTES.login}
                                                        loginComponent={LoginLanding}
                                                        noAccessRoute={ROUTES.nonstudent}
                                                        noAccessComponent={NoAccessPage}
                                                        landingRoute={ROUTES.landing}
                                                        delayedRoutes={delayedRoutes}
                                                        isLoginHandledExternally
                                                    >
                                                        <ScrollToTop />
                                                        <FeeWaiverProvider>
                                                            <Suspense fallback={<Spinner />}>
                                                                <SessionTimeoutManager
                                                                    redirectUrl={import.meta.env.VITE_MY_SAT_URL}
                                                                >
                                                                    <AppSelector awsRum={awsRum} />
                                                                </SessionTimeoutManager>
                                                            </Suspense>
                                                        </FeeWaiverProvider>
                                                    </LoginManager>
                                                    <BackToTop topOfPageAnchorId="mysat-reg-homepage" />
                                                </div>
                                            </AppTokenProvider>
                                        </AppFlowAnalyzer>
                                    </AuthProvider>
                                </CaptchaProvider>
                            </EnvironmentProvider>
                        </ServiceErrorBoundary>
                    </div>
                </DrupalInstanceProvider>
            </BrowserRouter>
            <GlobalFooter />
        </WebAnalyticsProvider>
    );
};

const ldClientSideID = import.meta.env.VITE_LD_CLIENT_KEY as string | undefined;

const AppWithLD = ldClientSideID
    ? withLDProvider({
          clientSideID: ldClientSideID,
          context: { kind: 'user', anonymous: true, key: 'anonymous' },
      })(App)
    : App;

export default AppWithLD;
